import { NOTIFICATION_WORKER_URL } from '@/lib/env'
import { getSupabaseAuthToken } from '@/lib/supabaseClient'

async function authedPost<T>(path: string, body: unknown): Promise<T> {
  if (!NOTIFICATION_WORKER_URL) {
    throw new Error('Notification worker URL is not configured. Set VITE_NOTIFICATION_WORKER_URL.')
  }
  const token = await getSupabaseAuthToken()
  const res = await fetch(`${NOTIFICATION_WORKER_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Notification worker error: ${text}`)
  }
  return res.json() as Promise<T>
}

export interface ContributorInviteEmailParams {
  dropId: string
  organizationId: string
  recipientEmail: string
  recipientName?: string
  inviterName?: string
  dropName: string
  contributorToken: string
}

export interface ContributorInviteSmsParams {
  dropId: string
  organizationId: string
  recipientPhone: string
  recipientName?: string
  inviterName?: string
  dropName: string
  contributorToken: string
  consentConfirmed: boolean
}

export interface TeamInviteEmailParams {
  organizationId: string
  email: string
  role: 'owner' | 'admin' | 'manager' | 'viewer'
  organizationName: string
  inviterName?: string
}

export function sendContributorInviteEmail(params: ContributorInviteEmailParams) {
  return authedPost<{ sent: boolean; invitationId: string }>('/contributor-invite-email', params)
}

export function sendContributorInviteSms(params: ContributorInviteSmsParams) {
  return authedPost<{ sent: boolean; invitationId: string }>('/contributor-invite-sms', params)
}

export function resendContributorInvite(invitationId: string) {
  return authedPost<{ resent: boolean }>('/resend-contributor-invite', { invitationId })
}

export function cancelContributorInvite(invitationId: string) {
  return authedPost<{ cancelled: boolean }>('/cancel-contributor-invite', { invitationId })
}

export function sendTeamInviteEmail(params: TeamInviteEmailParams) {
  return authedPost<{ sent: boolean; invitationId: string }>('/team-invite-email', params)
}

export function acceptTeamInvite(token: string) {
  return authedPost<{ accepted: boolean; organizationId: string }>('/accept-team-invite', { token })
}

export async function fetchTeamInvite(token: string) {
  if (!NOTIFICATION_WORKER_URL) {
    throw new Error('Notification worker URL is not configured.')
  }
  const url = new URL(`${NOTIFICATION_WORKER_URL}/team-invite`)
  url.searchParams.set('token', token)
  const res = await fetch(url.toString())
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Failed to fetch invite: ${text}`)
  }
  return (await res.json()) as {
    organizationId: string
    email: string
    role: 'owner' | 'admin' | 'manager' | 'viewer'
    expiresAt: string
  }
}
